import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Dropdown, Menu, MenuProps, Tooltip } from "antd";
import { 
  QuestionOutlined, 
  LogoutOutlined,
  ArrowLeftOutlined,
  DownloadOutlined,
  ArrowRightOutlined
} from "@ant-design/icons";
import { ReactComponent as AwardIcon } from "../../assets/icon/award.svg";
import { ReactComponent as BookOpenIcon } from "../../assets/icon/book-open-1.svg";
import { ReactComponent as BriefcaseIcon } from "../../assets/icon/briefcase-1.svg";
import { ReactComponent as DesajustIcon } from "../../assets/icon/desajust.svg";
import { ReactComponent as BrainIcon } from "../../assets/icon/brain.svg";

import styles from "./Aside.module.css";
import { PATH_ROUTES } from "../../router/routes";
import { useAuthContext, useMessageContext } from "../../providers";
import InformeUnificatModal from "../../components/Modals/InformeUnificat/InformeUnificatModal";
import { MouseEventHandler } from "react";

interface AsideProps {
  onCollapseChange?: (isCollapsed: boolean) => void;
}

const helpItems: MenuProps["items"] = [
  {
    key: "1",
    label: <a href="mailto:support@omnios.ai">Contacta amb el suport</a>,
  },
  {
    type: "divider",
  },
  {
    key: "2",
    label: (
      <a
        className={styles.omniosLink}
        href="https://omnios.ai/"
        target="_blank"
        rel="noreferrer"
      >
        © {new Date().getFullYear()} • Omnios
      </a>
    ),
  },
];

const modulesItems: MenuProps["items"] = [
  {
    key: "titulacions",
    label: "Titulacions",
    icon: (
      <span className="anticon">
        <AwardIcon />
      </span>
    ),
  },
  {
    key: "professions-regulades",
    label: "Professions regulades",
    icon: (
      <span className="anticon">
        <BrainIcon />
      </span>
    ),
  },
  {
    key: "professionals-sanitaris",
    label: "Professionals sanitaris",
    icon: (
      <span className="anticon">
        <BriefcaseIcon />
      </span>
    ),
  },
  {
    key: "oferta-universitaria",
    label: "Oferta universitaria",
    icon: (
      <span className="anticon">
        <BookOpenIcon />
      </span>
    ),
  },
  {
    key: "desajust-titulacio",
    label: "Desajust per titulació",
    icon: (
      <span className="anticon">
        <DesajustIcon />
      </span>
    ),
  },
];

const actionsItems: MenuProps["items"] = [
  {
    key: "informe-unificat",
    label: "Informe unificat",
    icon: <DownloadOutlined />,
  },
];

const MODULE_TO_PATH: Record<string, string> = {
  titulacions: PATH_ROUTES.home,
  "professions-regulades": PATH_ROUTES.professionsRegulades,
  "professionals-sanitaris": PATH_ROUTES.professionalsSanitaris,
  "oferta-universitaria": PATH_ROUTES.ofertaUniversitaria,
  "desajust-titulacio": PATH_ROUTES.desajustTitulacio,
};

const DOWNLOAD_LOADING_MESSAGE =
  "Generant arxiu... Ho veuràs en les teves descàrregues en un moment.";

const Aside = ({ onCollapseChange }: AsideProps) => {
  const { logout } = useAuthContext();
  const { sendMessage } = useMessageContext();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [informeUnificatModalOpen, setInformeUnificatModalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path === PATH_ROUTES.professionsRegulades) return ["professions-regulades"];
    if (path === PATH_ROUTES.professionalsSanitaris) return ["professionals-sanitaris"];
    if (path === PATH_ROUTES.ofertaUniversitaria) return ["oferta-universitaria"];
    if (path === PATH_ROUTES.desajustTitulacio) return ["desajust-titulacio"];
    return ["titulacions"];
  };

  const onSignOutClick: MouseEventHandler<HTMLButtonElement> = () => {
    logout();
  };

  const handleModuleClick = (key: string) => {
    const path = MODULE_TO_PATH[key];
    if (path) navigate(path);
  };

  const handleActionClick = (key: string) => {
    if (key === "informe-unificat") {
      setInformeUnificatModalOpen(true);
    }
  };

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onCollapseChange?.(newCollapsedState);
  };

  return (
    <aside className={`${styles.aside} ${isCollapsed ? styles.collapsed : ""}`}>
      <div className={styles.header}>
        <h2 className={styles.appTitle}>EstudIA</h2>
        <Button
          type="text"
          className={styles.collapseButton}
          icon={isCollapsed ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
          onClick={toggleCollapse}
        />
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>MÒDULS</h3>
        <Menu
          mode="inline"
          className={styles.menu}
          selectedKeys={getSelectedKeys()}
          items={modulesItems}
          onClick={(info) => handleModuleClick(info.key)}
        />
      </div>

      <div className={styles.divider}></div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>ACCIONS</h3>
        <Menu
          mode="inline"
          className={`${styles.menu} ${styles.menuActions}`}
          selectedKeys={informeUnificatModalOpen ? ["informe-unificat"] : []}
          items={actionsItems}
          onClick={(info) => handleActionClick(info.key)}
        />
      </div>

      <div className={styles.footer}>
        <Tooltip title="Sortir" placement="right">
          <Button className={styles.signOutButton} size="small" icon={<LogoutOutlined />} onClick={onSignOutClick}/>
        </Tooltip>
        <Dropdown placement="topLeft" menu={{ items: helpItems }} trigger={["click"]}>
          <Tooltip title="Suport" placement="right">
            <Button size="small" icon={<QuestionOutlined />} />
          </Tooltip>
        </Dropdown>
      </div>

      <InformeUnificatModal
        open={informeUnificatModalOpen}
        onCancel={() => setInformeUnificatModalOpen(false)}
        onDownload={() => {
          setInformeUnificatModalOpen(false);
          sendMessage(DOWNLOAD_LOADING_MESSAGE, "loading", 0, "informe-unificat-download");
          // TODO: lógica de descarga; després sendMessage(..., "success") o "error"
        }}
      />
    </aside>
  );
};
export default Aside;
